import * as React from "react";
import GlobalStyles from "@mui/joy/GlobalStyles";
import Avatar from "@mui/joy/Avatar";
import Box from "@mui/joy/Box";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import SupportRoundedIcon from "@mui/icons-material/SupportRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import FeedbackIcon from "@mui/icons-material/Feedback";

import ColorSchemeToggle from "./ColorSchemeToggle";
import { closeSidebar } from "../utils";
import { AccountInfo } from "@azure/msal-browser";
import { useHash } from "react-use";

export default function Sidebar({ account }: any) {
  const [currentUser, setAccount] = React.useState<AccountInfo>();
  const [hash, setHash] = useHash();

  const handleFeedbackClick = () => {
    const email = "help.servicecloud@ef.com";
    const subject = "Support Form Feedback";

    const mailtoUrl = `mailto:${email}?subject=${subject}`;
    window.open(mailtoUrl, "_blank");
  };

  React.useEffect(() => {
    setAccount(account);
  }, [account]);

  if (currentUser) {
    return (
      <Sheet
        className="Sidebar"
        sx={{
          position: { xs: "fixed", md: "sticky" },
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
            md: "none",
          },
          transition: "transform 0.4s, width 0.4s",
          zIndex: 10000,
          height: { xs: "100dvh", md: "calc(100dvh - var(--Header-height, 0px))" },
          width: "var(--Sidebar-width)",
          top: { xs: 0, md: "var(--Header-height, 0px)" },
          p: 2,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            ":root": {
              "--Sidebar-width": "280px",
              [theme.breakpoints.up("lg")]: {
                "--Sidebar-width": "280px",
              },
            },
          })}
        />
        <Box
          className="Sidebar-overlay"
          sx={{
            position: "fixed",
            zIndex: 9998,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            opacity: "var(--SideNavigation-slideIn)",
            backgroundColor: "var(--joy-palette-background-backdrop)",
            transition: "opacity 0.4s",
            transform: {
              xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
              lg: "translateX(-100%)",
            },
          }}
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <IconButton variant="soft" color="primary" size="sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" viewBox="0 0 24 24" fill="currentColor" data-first-child="">
              <path d="m10.82 14.87-.58 2.75H2L4.41 6h8.24l-.56 2.7H7.53l-.34 1.66h4.45l-.56 2.74H6.62l-.36 1.76zM22 6l-.56 2.7H17.3l-.43 2.1h4l-.56 2.74h-4.02l-.84 4.08h-3.94L13.92 6z"></path>
            </svg>
          </IconButton>
          <Typography level="title-md">Helpdesk</Typography>
          <ColorSchemeToggle sx={{ ml: "auto" }} />
        </Box>

        <Box
          sx={{
            minHeight: 0,
            overflow: "hidden auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            [`& .${listItemButtonClasses.root}`]: {
              gap: 1.5,
            },
          }}
        >
          <List
            size="sm"
            sx={{
              gap: 1,
              "--List-nestedInsetStart": "30px",
              "--ListItem-radius": (theme) => theme.vars.radius.sm,
            }}
          >
            <ListItem>
              <ListItemButton
                selected={hash === "#tickets"}
                onClick={() => {
                  setHash("tickets");
                  closeSidebar();
                }}
              >
                <SupportRoundedIcon />
                My Tickets
              </ListItemButton>
            </ListItem>

            {import.meta.env.VITE_NODE_ENV === "development" && (
              <ListItem>
                <ListItemButton
                  selected={hash === "#new"}
                  onClick={() => {
                    setHash("new");
                    closeSidebar();
                  }}
                >
                  <PlaylistAddIcon />
                  New Ticket
                </ListItemButton>
              </ListItem>
            )}

            <ListItem sx={{ marginTop: "auto" }}>
              <ListItemButton onClick={handleFeedbackClick}>
                <FeedbackIcon />
                Feedback
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Divider />

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Avatar
            variant="outlined"
            size="sm"
          />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography level="title-sm">{currentUser.name}</Typography>
            <Typography level="body-xs">{currentUser.username}</Typography>
          </Box>
          <IconButton size="sm" variant="plain" color="neutral">
            <LogoutRoundedIcon />
          </IconButton>
        </Box>
      </Sheet>
    );
  } else {
    return null;
  }
}
